import { Box, type SxProps, type Theme } from '@mui/material';
import { createContext, forwardRef, useContext, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useCountAsOpenToolTray } from '~/components/toolbar/shared/ToolTrayPresenceProvider';

/**
 * The element a toolbar lays its open tray out in, below the toolbar and in
 * flow, so whatever the toolbar stacks under it moves down instead of being
 * covered. Null outside such a toolbar.
 */
export const ToolPropertiesTraySlotContext = createContext<HTMLElement | null>(
    null
);

interface ToolPropertiesTrayProps {
    children: ReactNode;
    sx?: SxProps<Theme>;
    /**
     * `toolbar` lays the tray out in the enclosing toolbar's tray slot.
     * `parent` floats it below its parent element, over whatever is there,
     * for a tray that shows only while it is hovered.
     */
    placement?: 'toolbar' | 'parent';
}

/**
 * Floating glassmorphic properties bar that renders below a toolbar. Without
 * a tray slot to lay out in, it floats below its parent element. While
 * mounted it counts as an open tray of the enclosing
 * {@link ToolTrayPresenceProvider} scope.
 */
const ToolPropertiesTray = forwardRef<HTMLDivElement, ToolPropertiesTrayProps>(
    function ToolPropertiesTray(
        { children, sx: sxOverrides, placement = 'toolbar', ...rest },
        ref
    ) {
        useCountAsOpenToolTray();
        const slot = useContext(ToolPropertiesTraySlotContext);
        const inSlot = placement === 'toolbar' && slot !== null;
        const tray = (
            <Box
                ref={ref}
                {...rest}
                // Counts as part of the toolbar wherever it is laid out, so a
                // press on it is not a click away from an open tool panel.
                data-toolbar-id="tool-properties-tray"
                sx={[
                    (theme) => ({
                        display: 'flex',
                        alignItems: 'center',
                        px: 1,
                        py: '3px',
                        ...theme.mixins.floatingSurface,
                        borderRadius: 100,
                        pointerEvents: 'auto',
                        whiteSpace: 'nowrap',
                    }),
                    !inSlot && {
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        mt: 1,
                    },
                    ...(Array.isArray(sxOverrides)
                        ? sxOverrides
                        : sxOverrides
                          ? [sxOverrides]
                          : []),
                ]}
            >
                {children}
            </Box>
        );
        return inSlot ? createPortal(tray, slot) : tray;
    }
);

export default ToolPropertiesTray;
